import { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDialogStyles, useFormFieldStyles, useSectionStyles } from '@/styles/utilities/hooks';
import api from '../../services/api';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export default function UserRegister({ onSuccess, onCancel }) {
  const theme = useTheme();
  const dialogStyles = useDialogStyles({ 
    maxWidth: theme.spacing(62),
    minWidth: theme.spacing(50)  // Ensure proportional scaling
  });
  const formFieldStyles = useFormFieldStyles();
  const sectionStyles = useSectionStyles();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullname: '',
    company: '',
    department: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const validateForm = () => {
    if (formData.username.length < 4) {
      setError('Username must be at least 4 characters long');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        fullname: formData.fullname || null,
        company: formData.company || null,
        department: formData.department || null
      };

      await api.post(API_ENDPOINTS.AUTH.REGISTER, payload);
      
      setSuccess('Registration successful! Please wait for admin approval.');
      setTimeout(() => {
        onSuccess && onSuccess();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(2)
      }}
    >
      <Paper
        elevation={3}
        sx={{
          ...dialogStyles.paper,
          padding: theme.spacing(4),
          borderRadius: theme.spacing(1)
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(1),
          mb: theme.spacing(3)
        }}>
          <Typography 
            component="h1" 
            align="center"
            sx={{ 
              fontSize: theme.typography.fontSize['2xl'],
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.palette.text.secondary
            }}
          >
            User Registration
          </Typography>
          <Typography sx={{ 
            fontSize: theme.typography.fontSize.base,
            color: theme.palette.text.secondary 
          }}>
            Register for Protify Access
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: theme.spacing(2) }}>
            {success}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1.5)
          }}
        >
          <TextField
            label="Username"
            value={formData.username}
            onChange={handleChange('username')}
            required
            helperText="Minimum 4 characters"
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />
          
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            required
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            required
            helperText="Minimum 6 characters"
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <TextField
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            required
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <TextField
            label="Full Name"
            value={formData.fullname}
            onChange={handleChange('fullname')}
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <TextField
            label="Company"
            value={formData.company}
            onChange={handleChange('company')}
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <TextField
            label="Department"
            value={formData.department}
            onChange={handleChange('department')}
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(42)
            }}
          />

          <Box sx={{ 
            mt: theme.spacing(2), 
            display: 'flex', 
            gap: theme.spacing(2),
            width: theme.spacing(42)
          }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              size="medium"
              sx={{ 
                height: theme.dimensions.button.medium,
                textTransform: 'none'
              }}
            >
              {loading ? <CircularProgress size={theme.spacing(3)} color="inherit" /> : 'Register'}
            </Button>
            
            {onCancel && (
              <Button
                variant="outlined"
                fullWidth
                onClick={onCancel}
                disabled={loading}
                size="medium"
                sx={{ 
                  height: theme.dimensions.button.medium,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
